import { performance } from 'perf_hooks';
import { fdir as FastDirectoryCrawler } from 'fdir';
import { Injectable } from '@gitlab-org/di';
import { URI } from 'vscode-uri';
import { WorkspaceFolder } from 'vscode-languageserver-protocol';
import chokidar from 'chokidar';
import {
  DirectoryWalker,
  DirectoryToSearch,
  FileChangeHandler,
} from '../../../common/services/fs/dir';
import { log } from '../../../common/log';
import { fsPathFromUri, fsPathToUri, parseURIString } from '../../../common/services/fs/utils';

/**
 * A directory walker that uses the desktop filesystem to search for files.
 * IMPORTANT NOTE: you must convert files that come from the file system to URIs
 * All filters (such as `DirectoryToSearch` filter args) are UNIX styled, so to ensure
 * cross-platform compatibility, you must convert the file paths to URIs.
 * We use the `vscode-uri` package to do this conversion.
 */
@Injectable(DirectoryWalker, [])
export class DesktopDirectoryWalker implements DirectoryWalker {
  #watchers: Map<string, chokidar.FSWatcher> = new Map();

  #applyFilters(fileUri: URI, filters: DirectoryToSearch['filters']) {
    if (!filters) {
      return true;
    }
    if (filters.fileEndsWith) {
      return filters.fileEndsWith.some((end) => fileUri.toString().endsWith(end));
    }
    return true;
  }

  async findFilesForDirectory(args: DirectoryToSearch): Promise<URI[]> {
    const { filters, directoryUri } = args;
    const perf = performance.now();
    const pathMap = new Map<string, URI>();
    const promise = new FastDirectoryCrawler()
      .withFullPaths()
      .filter((path) => {
        const fileUri = fsPathToUri(path);
        // save each normalized filesystem path avoid re-parsing the path
        pathMap.set(path, fileUri);
        return this.#applyFilters(fileUri, filters);
      })
      .crawl(fsPathFromUri(directoryUri))
      .withPromise();
    const paths = await promise;
    log.debug(
      `DirectoryWalker: found ${paths.length} paths, took ${Math.round(performance.now() - perf)}ms`,
    );
    return paths.map((path) => pathMap.get(path)) as URI[];
  }

  /**
   * Sets up a file watcher for the given workspace folder.
   * This uses chokidar to watch the files in the workspace folder.
   */
  setupFileWatcherForWorkspace(
    workspaceFolder: WorkspaceFolder,
    fileChangeHandler: FileChangeHandler,
  ): void {
    const fsPath = fsPathFromUri(parseURIString(workspaceFolder.uri));
    const watcher = chokidar.watch(fsPath, {
      persistent: true,
      alwaysStat: false,
      ignoreInitial: true,

      /**
       * TODO: make this configurable. https://gitlab.com/gitlab-org/gitlab/-/issues/485509
       */
      ignored: [
        // ---- GIT ----
        '**/.git/hooks/**',
        '**/.git/info/**',
        '**/.git/lfs/**',
        '**/.git/logs/**',
        '**/.git/objects/**',
        '**/.git/refs/**',
        '**/.git/packed-refs',
        /**
         * We are interested in tracking these files for `RepositoryService`,
         * so don't ignore them
         */
        '!**/.git/info/exclude',
        '!**/.git/HEAD',
        '!**/.git/index',
        '!**/.git/refs/heads/**',
        '!**/.git/refs/remotes/**',
        '!**/.git/refs/tags/**',
        '!**/.gitignore',
        '!**/.gitmodules',
        // ---- JS ----
        '**/node_modules/**',
        '**/dist/**',
        '**/build/**',
        '**/coverage/**',
        // ---- OTHER ----
        '**/vendor/**',
        '**/log/**',
        '**/cache/**',
        '**/public/assets/**',
        '**/storage/**',
        '**/bin/**',
        '**/obj/**',
        '**/target/**',
        // ---- RUBY ----
        '**/.bundle/**',
        // ---- IDE ----
        '**/.idea/**',
        '**/.vscode/**',
      ],
    });

    watcher
      .on('add', (path) => fileChangeHandler('add', workspaceFolder, path))
      .on('change', (path) => fileChangeHandler('change', workspaceFolder, path))
      .on('unlink', (path) => fileChangeHandler('unlink', workspaceFolder, path));

    this.#watchers.set(workspaceFolder.uri, watcher);
  }

  async disposeWatcherForWorkspace(workspaceFolder: WorkspaceFolder): Promise<void> {
    const watcher = this.#watchers.get(workspaceFolder.uri);
    if (watcher) {
      await watcher.close();
      this.#watchers.delete(workspaceFolder.uri);
    }
  }
}
